import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useThemeMode } from "../../ThemeContext";
import { APP_URLS } from "../../helpers/url_helper";

const getDefaultValue = (field) => {
  if (field?.type === "checkbox") return [];
  return "";
};

function CompanyFormFill() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const template = location.state?.template || null;
  const [submitMessage, setSubmitMessage] = useState("");
  const [formValues, setFormValues] = useState(() => {
    const values = {};
    const fields = Array.isArray(template?.schema_json) ? template.schema_json : [];
    fields.forEach((field) => {
      values[field.id] = getDefaultValue(field);
    });
    return values;
  });

  const fields = useMemo(
    () => (Array.isArray(template?.schema_json) ? template.schema_json : []),
    [template],
  );

  const updateValue = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field.id]: value }));
  };

  const updateCheckboxValue = (field, option) => {
    const current = Array.isArray(formValues[field.id]) ? formValues[field.id] : [];
    if (current.includes(option)) {
      updateValue(field, current.filter((item) => item !== option));
    } else {
      updateValue(field, [...current, option]);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitMessage("Form is filled successfully. Submission API can be connected next.");
  };

  if (!template || !fields.length) {
    return (
      <div style={{ padding: 20 }}>
        <div style={emptyStateStyle(mode)}>
          No form selected. Please choose a form from Company Forms list.
        </div>
        <button
          type="button"
          onClick={() => navigate(APP_URLS.COMPANY_FORMS)}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to Company Forms
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={wrapperStyle(mode)}>
        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => navigate(APP_URLS.COMPANY_FORMS)}
            style={backButtonStyle(mode)}
          >
            <ArrowBackIcon style={{ fontSize: 18 }} />
            Back to Company Forms
          </button>
          <h2 style={titleStyle(mode)}>{template?.title || "Company Form"}</h2>
          <p style={descriptionStyle(mode)}>
            {template?.description || "Please fill the form below."}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
          {fields.map((field) => (
            <div key={field.id} style={fieldWrapStyle(mode)}>
              <label style={labelStyle(mode)}>
                {field.label || "Untitled Field"}
                {field.required ? " *" : ""}
              </label>
              {renderInputField(field, formValues, mode, updateValue, updateCheckboxValue)}
            </div>
          ))}

          <div>
            <button type="submit" style={submitButtonStyle()}>
              Submit
            </button>
          </div>
          {submitMessage && <div style={messageStyle(mode)}>{submitMessage}</div>}
        </form>
      </div>
    </div>
  );
}

function renderInputField(field, formValues, mode, updateValue, updateCheckboxValue) {
  const commonInputStyle = inputStyle(mode);
  const fieldValue = formValues[field.id];
  const options = Array.isArray(field.options) ? field.options : [];

  if (field.type === "textarea") {
    return (
      <textarea
        required={Boolean(field.required)}
        value={fieldValue || ""}
        onChange={(event) => updateValue(field, event.target.value)}
        style={{ ...commonInputStyle, minHeight: 90, resize: "vertical" }}
        placeholder={field.placeholder || "Enter value"}
      />
    );
  }

  if (field.type === "dropdown") {
    return (
      <select
        required={Boolean(field.required)}
        value={fieldValue || ""}
        onChange={(event) => updateValue(field, event.target.value)}
        style={commonInputStyle}
      >
        <option value="">Select option</option>
        {options.map((option, index) => (
          <option key={`${field.id}-${index}`} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "radio") {
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {options.map((option, index) => (
          <label key={`${field.id}-${index}`} style={choiceLabelStyle(mode)}>
            <input
              type="radio"
              name={field.id}
              checked={fieldValue === option}
              onChange={() => updateValue(field, option)}
              required={Boolean(field.required) && !fieldValue}
            />
            {option}
          </label>
        ))}
      </div>
    );
  }

  if (field.type === "checkbox") {
    const selected = Array.isArray(fieldValue) ? fieldValue : [];
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {options.map((option, index) => (
          <label key={`${field.id}-${index}`} style={choiceLabelStyle(mode)}>
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => updateCheckboxValue(field, option)}
            />
            {option}
          </label>
        ))}
      </div>
    );
  }

  const htmlTypeMap = {
    text: "text",
    email: "email",
    number: "number",
    date: "date",
  };

  return (
    <input
      required={Boolean(field.required)}
      type={htmlTypeMap[field.type] || "text"}
      value={fieldValue || ""}
      onChange={(event) => updateValue(field, event.target.value)}
      style={commonInputStyle}
      placeholder={field.placeholder || "Enter value"}
    />
  );
}

const wrapperStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  maxWidth: 900,
});

const titleStyle = (mode) => ({
  margin: "14px 0 6px",
  color: mode === "dark" ? "#fff" : "#0f172a",
});

const descriptionStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#98a2b3" : "#64748b",
  fontSize: 13,
});

const emptyStateStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  marginBottom: 12,
});

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const fieldWrapStyle = (mode) => ({
  display: "grid",
  gap: 6,
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#f1f5f9"}`,
  borderRadius: 10,
  padding: 12,
});

const labelStyle = (mode) => ({
  color: mode === "dark" ? "#e4e7ec" : "#1e293b",
  fontSize: 13,
  fontWeight: 600,
});

const inputStyle = (mode) => ({
  width: "100%",
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const choiceLabelStyle = (mode) => ({
  display: "inline-flex",
  gap: 8,
  alignItems: "center",
  color: mode === "dark" ? "#e4e7ec" : "#334155",
  fontSize: 13,
});

const submitButtonStyle = () => ({
  border: "none",
  borderRadius: 8,
  background: "#009391",
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  padding: "10px 16px",
  cursor: "pointer",
});

const messageStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#2e6b69" : "#9be3df"}`,
  background: mode === "dark" ? "#122c2b" : "#ecfbfa",
  color: mode === "dark" ? "#8de0db" : "#0c6a65",
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 13,
});

export default CompanyFormFill;
